import {
  ALL_TIME_DATE_WINDOW,
  createLastNWindow,
  DataPointsApiResponse,
  DataRow,
  DataTable,
  getDefinitionWithServerOrDefaultWindow,
  getMeasureFormatString,
  materializeTable,
  maxPrecision,
  parseDataPoints,
} from '@tableau/metrics-engine';
import { MetricCardProps, MetricDetailViewProps } from '@tableau/metrics-shared-widgets-dom';

import { FeatureFlags } from '../ts/FeatureFlags';
import { IMetricInfo } from './models/IMetricInfo';
import { IMetricDataList } from './serverApi/types/IMetricDataList';

export const metricCardProps = (metric: DataPointsApiResponse): MetricCardProps => {
  const data = metric.data ? metric.data : [];
  const measureFormat = getMeasureFormatString(metric.definition);

  const defaultWindow = createLastNWindow(100);
  const definitionWithDefaultWindow = getDefinitionWithServerOrDefaultWindow(metric.definition, defaultWindow, FeatureFlags.metricsDateWindowEnabled);

  const materializedData = materializeTable(definitionWithDefaultWindow, data);
  const dataToRender = materializedData.unwrapOr([]);

  const metricsReferenceLineEnabled = FeatureFlags.metricsReferenceLineEnabled;

  return {
    testId: 'metric-card',
    data: dataToRender,
    definition: definitionWithDefaultWindow,
    lastUpdatedTimestamp: metric.dataRefreshedTimestamp,
    measureFormat,
    suspended: false,
    metricColorIndicatorsEnabled: FeatureFlags.metricsColorIndicatorsEnabled,
    metricsReferenceLineEnabled,
  };
};

export const getTableWithDefaultComparison = (metric: DataPointsApiResponse): DataTable => {
  const data = metric.data ? metric.data : [];
  const definition = getDefinitionWithServerOrDefaultWindow(metric.definition, createLastNWindow(100), FeatureFlags.metricsDateWindowEnabled);

  const materializedData = materializeTable(definition, data);
  return materializedData.unwrapOr([]);
};

export const getMergedMetricInfo = (metricInfo: IMetricInfo, metricDataList: IMetricDataList): IMetricInfo => {
  const data = metricDataList.data[metricInfo.id];
  if (data) {
    return { ...metricInfo,
      dataPoints: parseDataPoints(data.points, maxPrecision(data.schema?.measure?.format)),
      schema: data.schema,
    dataRefreshedTimestamp: data.dataRefreshedTimestamp };
  } else {
    return metricInfo;
  }
};

export const getMetricMeasure = (metric: DataPointsApiResponse | undefined, measureAccessor: (point: DataRow) => number): number | undefined => {
  return metric?.data?.length > 0 && measureAccessor(metric.data[metric.data.length - 1]);
};

export const metricDetailProps = (name: string, metric?: DataPointsApiResponse): MetricDetailViewProps => {
  const data = metric?.data ? metric.data : [];

  const definitionWithWindow = metric?.definition && getDefinitionWithServerOrDefaultWindow(metric.definition, ALL_TIME_DATE_WINDOW, FeatureFlags.metricsDateWindowEnabled);
  const measureFormat = definitionWithWindow && getMeasureFormatString(definitionWithWindow);

  const dataToRender = materializeTable(definitionWithWindow, data).unwrapOr([]);

  return {
    metricName: name,
    testId: 'metric-detail',
    data: dataToRender,
    definition: definitionWithWindow,
    lastUpdatedTimestamp: metric?.dataRefreshedTimestamp,
    measureFormat,
    metricsReferenceLineEnabled: FeatureFlags.metricsReferenceLineEnabled,
    metricColorIndicatorsEnabled: FeatureFlags.metricsColorIndicatorsEnabled,
  };
};

